import { Validator } from '~/workera/utils/typedForms/validators/common/Validator'

import { Icon } from '~/workera/utils/typedForms/shared/graphic/Icon'
import { Option, None } from 'space-lift'
import {
  ValidatorResponse,
  ValidatorResponseType,
} from '~/workera/utils/typedForms/validators/common/ValidatorResponse'
import {
  ModifierResponse,
  ModifierResponseType,
} from '../../modifiers/common/ModifierResponse'
import { Modifier } from '~/workera/utils/typedForms/modifiers/common/Modifier'

export enum FormFieldType {
  CHECKBOX,
  RADIO,
  PASSWORD,
  SIMPLE_SELECT,
  TEXTAREA,
  TWEAK,
  INPUT,
  FILE,
  TELEPHONE,
  MULTISELECT,
  URL_FIELD,
  TOGGLE,
}

export abstract class Field<T> {
  abstract required: boolean
  abstract fieldValidators: Validator<T>[]
  abstract fieldModifiers: Modifier<T>[]
  abstract value: T
  abstract label: string
  abstract readonly fieldType: FormFieldType
  abstract key: string
  abstract validationText: Option<string>

  validate(): ValidatorResponse {
    let validations: string[] = []
    let validationsErrors: string[] = []

    for (var index in this.fieldValidators) {
      const result: ValidatorResponse = this.fieldValidators[index].validate(
        this.value
      )
      if (result.validatorType !== ValidatorResponseType.SUCCESS) {
        const validation = this.validationText.fold(
          () => result,
          text => new ValidatorResponse(result.validatorType, [text])
        )
        validationsErrors.push(validation.message[0])
      } else {
        validations.concat(result.message)
      }
    }
    if (validationsErrors.length > 0) {
      return new ValidatorResponse(
        ValidatorResponseType.ERROR,
        validationsErrors
      )
    }

    return new ValidatorResponse(ValidatorResponseType.SUCCESS, validations)
  }

  modify(event: Option<T> = None): ModifierResponse<T> {
    const initialResult: T = event.fold(() => this.value, _event => _event)

    return this.fieldModifiers.reduce((prev, curr) => {
      const currResult = curr.modify(prev.result)
      const currState =
        prev.isModified || currResult.isModified
          ? ModifierResponseType.MODIFIED
          : ModifierResponseType.UNMODIFIED

      return new ModifierResponse(currState, currResult.result)
    }, new ModifierResponse(ModifierResponseType.UNMODIFIED, initialResult))
  }
}

export class CheckboxField extends Field<string[]> {
  readonly fieldType = FormFieldType.CHECKBOX

  stacked: boolean
  value: string[]
  options: string[]
  key: string
  fieldValidators: Validator<string[]>[]
  fieldModifiers: Modifier<string[]>[]
  required: boolean
  label: string
  validationText: Option<string>

  constructor(
    key: string,
    label: string,
    fieldValidators: Validator<string[]>[],
    fieldModifiers: Modifier<string[]>[],
    required: boolean,
    options: string[],
    stacked: boolean,
    value: string[] = [],
    validationText: Option<string> = None
  ) {
    super()

    this.fieldValidators = fieldValidators
    this.fieldModifiers = fieldModifiers
    this.required = required
    this.label = label
    this.options = options
    this.validationText = validationText
    this.value = value
    this.stacked = stacked
    this.key = key
  }
}

export class RadioField extends Field<string> {
  readonly fieldType = FormFieldType.RADIO

  stacked: boolean
  value: string
  options: string[]
  key: string
  fieldValidators: Validator<string>[]
  fieldModifiers: Modifier<string>[]
  required: boolean
  label: string
  validationText: Option<string>

  constructor(
    key: string,
    label: string,
    fieldValidators: Validator<string>[],
    fieldModifiers: Modifier<string>[],
    required: boolean,
    options: string[],
    stacked: boolean,
    value: string = '',
    validationText: Option<string> = None
  ) {
    super()

    this.fieldValidators = fieldValidators
    this.fieldModifiers = fieldModifiers
    this.required = required
    this.label = label
    this.options = options
    this.value = value
    this.validationText = validationText
    this.stacked = stacked
    this.key = key
  }
}

export class MultiselectField extends Field<string[]> {
  readonly fieldType = FormFieldType.MULTISELECT

  value: string[]
  options: string[]
  key: string
  fieldValidators: Validator<string[]>[]
  fieldModifiers: Modifier<string[]>[]
  required: boolean
  label: string
  validationText: Option<string>
  placeholder: String

  constructor(
    key: string,
    label: string,
    fieldValidators: Validator<string[]>[],
    fieldModifiers: Modifier<string[]>[],
    required: boolean,
    options: string[],
    placeholder: String,
    value: string[] = [],
    validationText: Option<string> = None
  ) {
    super()

    this.fieldValidators = fieldValidators
    this.fieldModifiers = fieldModifiers
    this.required = required
    this.label = label
    this.options = options
    this.value = value
    this.validationText = validationText
    this.placeholder = placeholder
    this.key = key
  }
}

export interface SelectOption {
  value: string
  text: string
}

export class SimpleSelectField extends Field<string> {
  readonly fieldType = FormFieldType.SIMPLE_SELECT

  value: string
  options: SelectOption[]
  key: string
  fieldValidators: Validator<string>[]
  fieldModifiers: Modifier<string>[]
  required: boolean
  label: string
  validationText: Option<string>

  constructor(
    key: string,
    label: string,
    fieldValidators: Validator<string>[],
    fieldModifiers: Modifier<string>[],
    required: boolean,
    options: SelectOption[],
    validationText: Option<string> = None,
    value: string = ''
  ) {
    super()

    this.fieldValidators = fieldValidators
    this.fieldModifiers = fieldModifiers
    this.required = required
    this.label = label
    this.options = options
    this.value = value
    this.validationText = validationText
    this.key = key
  }
}

export abstract class GeneralInputField extends Field<string> {
  label: string
  icon: Option<Icon>
  value: string
  required: boolean
  key: string
  placeholder: string
  validationText: Option<string>
  fieldValidators: Validator<string>[]
  fieldModifiers: Modifier<string>[]
  sharedRow: boolean
  marginRight: boolean
  autocomplete: string

  abstract type: string

  constructor(
    key: string,
    label: string,
    required: boolean,
    placeholder: string,
    fieldValidators: Validator<string>[],
    fieldModifiers: Modifier<string>[],
    value: string = '',
    validationText: Option<string> = None,
    icon: Option<Icon> = None,
    sharedRow: boolean = false,
    marginRight: boolean = false,
    autocomplete: string = 'on'
  ) {
    super()
    this.label = label
    this.required = required
    this.value = value
    this.icon = icon
    this.key = key
    this.validationText = validationText
    this.placeholder = placeholder
    this.fieldValidators = fieldValidators
    this.fieldModifiers = fieldModifiers
    this.sharedRow = sharedRow
    this.marginRight = marginRight
    this.autocomplete = autocomplete
  }
}

export class TextField extends GeneralInputField {
  readonly fieldType = FormFieldType.TEXTAREA
  readonly type = 'text'
}

export class TelephoneField extends GeneralInputField {
  readonly fieldType = FormFieldType.TELEPHONE
  readonly type = 'tel'
}

export class PasswordField extends GeneralInputField {
  readonly fieldType = FormFieldType.PASSWORD
  readonly type = 'password'
}

export class InputField extends GeneralInputField {
  readonly fieldType = FormFieldType.INPUT
  readonly type = 'text'
}
